
import React, { useState } from 'react';
import { getYoutubeEmbedUrl, getVimeoEmbedUrl } from '@/utils/videoEmbed';
import { VideoConfig } from '@/types/videoConfig';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";

interface CountryFeaturedVideoProps {
  videoConfig: VideoConfig;
}

const CountryFeaturedVideo: React.FC<CountryFeaturedVideoProps> = ({ videoConfig }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  
  if (!videoConfig || !videoConfig.enabled || !videoConfig.video_id) {
    return null;
  }
  
  const getEmbedUrl = () => {
    if (videoConfig.platform === 'youtube') {
      return getYoutubeEmbedUrl(videoConfig.video_id);
    } else if (videoConfig.platform === 'vimeo') {
      return getVimeoEmbedUrl(videoConfig.video_id);
    }
    return '';
  };
  
  const embedUrl = getEmbedUrl();
  
  const handleLoad = () => {
    setIsLoading(false);
  };
  
  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <Collapsible 
        open={isOpen} 
        onOpenChange={setIsOpen} 
        className="w-full overflow-hidden bg-gray-100 dark:bg-gray-800 rounded-lg shadow-md"
      >
        <div className="flex items-center justify-between p-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
            {videoConfig.title}
          </h2>
          
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-9 w-9 p-0">
              {isOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              <span className="sr-only">Toggle featured video</span>
            </Button>
          </CollapsibleTrigger>
        </div>
        
        <CollapsibleContent>
          {/* Video loading indicator */}
          {isLoading && (
            <div className="relative h-20 flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nation-blue"></div>
            </div>
          )}
          
          {/* Error message */}
          {hasError && (
            <div className="p-4 text-center">
              <div className="text-red-500">
                <p className="font-semibold">Unable to load video</p>
                <p className="text-sm mt-1">Please try again later</p>
              </div>
            </div>
          )}
          
          {/* Video container with 16:9 aspect ratio */}
          <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
            <iframe
              title={videoConfig.title}
              className="absolute inset-0 w-full h-full"
              src={embedUrl}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onLoad={handleLoad}
              onError={handleError}
            ></iframe>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default CountryFeaturedVideo;
